/** Auto-clear delay for invite-payment success banner (aligned with registrar feedback tuning). */
export const INVITE_REGISTRATION_SUCCESS_AUTO_CLEAR_MS = 12000;

/**
 * Builds user-facing copy for POST /usuarios/registrar-y-enviar (admin registration).
 * @param {boolean} responseOk
 * @param {object} data Parsed JSON body from the API.
 * @returns {{ kind: "success" | "warning" | "error"; lines: string[]; autoClearMs: number }}
 */
export function buildRegistrarAdminUsuarioFeedback(responseOk, data) {
  if (!responseOk) {
    const err =
      data != null &&
      typeof data === "object" &&
      typeof /** @type {{ error?: unknown }} */ (data).error === "string" &&
      /** @type {{ error: string }} */ (data).error.trim() !== ""
        ? /** @type {{ error: string }} */ (data).error.trim()
        : "No se pudo completar el registro.";
    return { kind: "error", lines: [err], autoClearMs: 0 };
  }

  const record = data != null && typeof data === "object" && !Array.isArray(data) ? data : {};

  const recordError =
    typeof record.error === "string" && record.error.trim() !== "" ? record.error.trim() : "";
  if (recordError) {
    return { kind: "warning", lines: [recordError], autoClearMs: 0 };
  }

  const flow = typeof record.flow === "string" ? record.flow.trim() : "";
  const emailSent = record.emailSent !== false;
  const serverMensaje =
    typeof record.mensaje === "string" && record.mensaje.trim() !== "" ? record.mensaje.trim() : "";

  if (flow === "email_verification") {
    const lines = [];
    if (serverMensaje) {
      lines.push(serverMensaje);
    } else {
      lines.push("Cuenta de alumno creada.");
    }
    if (emailSent) {
      lines.push("Correo de verificación enviado al email indicado.");
    } else {
      lines.push("No se ha podido enviar el correo de verificación; revisa la configuración o el registro de actividad.");
    }
    const pending = record.pendingOrderId;
    if (pending != null && String(pending).trim() !== "") {
      lines.push(`Pedido pendiente de pago (referencia): ${String(pending).trim()}.`);
    }
    if (emailSent) {
      lines.push(
        "Siguiente paso del alumno: abrir el correo y verificar la cuenta. Los cursos de pago quedan pendientes hasta que pague.",
      );
    }
    return { kind: emailSent ? "success" : "warning", lines, autoClearMs: 0 };
  }

  if (flow === "staff_setup_email") {
    const lines = [];
    if (serverMensaje) {
      lines.push(serverMensaje);
    } else if (emailSent) {
      lines.push("Usuario registrado.");
    }
    if (emailSent) {
      lines.push("Correo con enlace para configurar la contraseña enviado.");
      lines.push(
        "El usuario debe usar ese enlace (24 h) o iniciar sesión si ya configuró la contraseña.",
      );
      return { kind: "success", lines, autoClearMs: 0 };
    }
    lines.push("El correo automático no se ha podido enviar; el usuario sigue creado en el sistema.");
    lines.push(
      "La contraseña no se incluye en la respuesta de la API: usa el gestor de usuarios o el flujo de restablecimiento de contraseña para que el usuario pueda acceder.",
    );
    const failureCode =
      typeof record.emailFailureCode === "string" && record.emailFailureCode.trim() !== ""
        ? record.emailFailureCode.trim()
        : "";
    if (failureCode) {
      lines.push(`Código de diagnóstico (sin datos sensibles): ${failureCode}.`);
    }
    return { kind: "warning", lines, autoClearMs: 0 };
  }

  const lines = [];
  if (serverMensaje) {
    lines.push(serverMensaje);
  } else {
    lines.push("Usuario registrado.");
  }
  const pwd = record.contraseñaMostrada;
  if (typeof pwd === "string" && pwd.trim() !== "") {
    lines.push(`Contraseña mostrada: ${pwd.trim()}`);
  }
  if (record.emailSent === true) {
    lines.push("Correo de bienvenida o acceso enviado.");
    return { kind: "success", lines, autoClearMs: 5000 };
  }
  if (record.emailSent === false) {
    lines.push(
      "El servidor indica que el correo no se ha enviado; revisa la configuración o el registro de actividad.",
    );
    return { kind: "warning", lines, autoClearMs: 0 };
  }
  lines.push(
    "Operación registrada. Si necesitas confirmar el envío de correo, revisa el registro de actividad (tipo «Registro de usuario»).",
  );
  return { kind: "success", lines, autoClearMs: 5000 };
}

/**
 * Reads JSON from a fetch Response; non-JSON bodies become a structured error object.
 * @param {Response} response
 * @returns {Promise<object>}
 */
export async function parseJsonFromResponse(response) {
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed === "") {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {
      error: `La respuesta del servidor no es JSON válido (código ${response.status}).`,
    };
  }
}
